import { EASE_OUT, EASE_LUSION } from './easing';
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import MagneticV2 from './MagneticV2';
import ArrowMark from './ArrowMark';
import { playOpen, playClose, isSfxMuted, setSfxMuted } from './sfx';
import { useSurfaceIsDark } from './useSurfaceIsDark';
import { trackInteraction } from '../../analytics';
import { RollSwap, useCopy } from './CopyPill';

interface NavV2Props {
  email: string;
  scrollTo: (selector: string) => void;
  started: boolean; // fade the bar in after the preloader
  phone?: string;
  socials?: { name: string; url: string }[];
  /** true while the reel is in theatre mode: the bar gets out of the way */
  hidden?: boolean;
}

const CV_PATH = '/cv/';

const LINKS = [
  { label: 'Showreel', target: '#top' },
  { label: 'Work', target: '#work' },
  { label: 'VibeCoding', target: '#vibecoding' },
  { label: 'About', target: '#about' },
  { label: 'Experience', target: '#experience' },
  { label: 'Contact', target: '#contact' },
];

const NavV2: React.FC<NavV2Props> = ({
  email,
  phone,
  socials = [],
  scrollTo,
  started,
  hidden = false,
}) => {
  const [open, setOpen] = useState(false);
  // sound is on by default; the choice persists per visitor
  const [sfxOff, setSfxOff] = useState(isSfxMuted());
  // Colour by what is actually painted under the pills, not a scroll guess —
  // this is what makes them flip on the contact card at the bottom too.
  const overHero = useSurfaceIsDark(() => ({ x: window.innerWidth - 120, y: 44 }));

  // The menu carries the same address and number as the contact card, so it
  // inherits the same guarantee: mailto:/tel: may do nothing at all, and a
  // copy always happens. Both hooks run unconditionally — `phone` is optional
  // and the link below is what is conditional, not the hook.
  // The CV is its own page (public/cv), readable on any screen and with the
  // PDF printed from it, so a recruiter never meets a browser's PDF viewer
  // unless they ask for the file. A new tab keeps the portfolio where it was.
  const openCv = () => trackInteraction('cv_view');

  const mailCopy = useCopy(email, `mailto:${email}`, () => trackInteraction('email_copy'));
  const telCopy = useCopy(phone ?? '', `tel:${(phone ?? '').replace(/[^+\d]/g, '')}`);

  const go = (target: string) => {
    setOpen(false);
    // let the overlay start closing before the scroll begins
    setTimeout(() => scrollTo(target), 350);
  };

  return (
    <>
      {/* A scrim, not a bar. The chrome floats over whatever is scrolling past,
          and a running line or a work-card title passing under the logo left
          both illegible; the logo used to answer that with a halo of its own,
          which read as a glow stuck to the letters. This blurs and fades the
          top of the viewport instead, so the logo and the pills always have
          something quiet beneath them.

          It sits at z-65, below the reel (z-70): the hero card fills the top of
          the screen at rest, and frosting a strip across it would fight the
          rounded frame. Everything the scrim is actually for is page content,
          which paints below it.

          The mask is what keeps it from reading as a header. Masking the
          element fades its backdrop-filter along with its tint, so the blur
          itself dissolves rather than ending on a line. */}
      <motion.div
        aria-hidden="true"
        className="fixed top-0 left-0 right-0 h-[88px] md:h-[104px] z-[65] pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: started && !hidden ? 1 : 0 }}
        transition={{ duration: 0.5, ease: EASE_OUT }}
        style={{
          // Blur only, no tint. A wash light enough not to read as a header
          // still had to pick a colour, and the card sections are inset, so
          // whichever it picked was wrong across the paper margin beside them.
          // Defocusing what passes underneath does the whole job on its own and
          // has nothing to get wrong over a dark surface.
          backdropFilter: 'blur(7px)',
          WebkitBackdropFilter: 'blur(7px)',
          maskImage: 'linear-gradient(to bottom, black 38%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to bottom, black 38%, transparent 100%)',
        }}
      />

      <motion.header
        // Three columns with the logo in the middle one, so it sits on the
        // viewport's centre line rather than centred in whatever is left of
        // the pills. The first column is empty and exists to balance the third.
        className="fixed top-0 left-0 right-0 z-[90] px-4 md:px-8 py-4 md:py-5 grid grid-cols-[1fr_auto_1fr] items-center pointer-events-none"
        // opacity only: the preloader measures #nav-logo to fly its lockup
        // here, so this element must already sit at its final position
        initial={{ opacity: 0 }}
        animate={{ opacity: started && !hidden ? 1 : 0 }}
        style={{ pointerEvents: started && !hidden ? 'auto' : 'none' }}
        transition={{ duration: 0.5, ease: EASE_OUT }}
      >
        {/* Invisible anchor only. The visible logo is the preloader's lockup,
            which flies here and stays for the life of the page — rendering a
            second one here would sit right on top of it. This element keeps
            the layout slot and gives Preloader an exact flight target. */}
        <span
          id="nav-logo"
          aria-hidden="true"
          className="col-start-2 justify-self-center font-grotesk font-semibold text-lg md:text-xl tracking-tight leading-none opacity-0 pointer-events-none select-none"
        >
          Hamlet Hayrapetyan
        </span>

        <div className="col-start-3 justify-self-end flex items-center gap-2 md:gap-3 pointer-events-auto">
          <MagneticV2>
            <a
              href={CV_PATH}
              onClick={openCv}
              target="_blank"
              rel="noopener"
              data-cursor="OPEN"
              className={`hidden md:flex items-center h-10 md:h-11 px-5 rounded-full border font-grotesk text-xs md:text-sm font-medium tracking-wide transition-colors duration-300 ${
                overHero
                  ? 'border-white/40 text-white hover:bg-white hover:text-ink'
                  : 'border-ink/15 text-ink hover:bg-ink/5'
              }`}
            >
              CV
            </a>
          </MagneticV2>
          <MagneticV2>
            {/* Takes you to the contact card rather than firing a mailto:
                straight at the visitor's mail client — the card is where the
                address, phone and socials actually live. */}
            <button
              onClick={() => scrollTo('#contact')}
              data-cursor="CONTACT"
              className={`hidden sm:flex items-center gap-2 h-10 md:h-11 px-5 rounded-full font-grotesk text-xs md:text-sm font-medium tracking-wide transition-colors duration-300 ${
                overHero
                  ? 'bg-white text-ink hover:bg-electric hover:text-white'
                  : 'bg-ink text-paper hover:bg-electric hover:text-white'
              }`}
            >
              Let's talk <span className="w-1 h-1 rounded-full bg-electric inline-block" />
            </button>
          </MagneticV2>
          <MagneticV2>
            <button
              onClick={() => { setOpen(true); playOpen(); }}
              data-cursor="OPEN"
              className={`flex items-center gap-2 h-10 md:h-11 px-5 rounded-full backdrop-blur text-ink font-grotesk text-xs md:text-sm font-medium tracking-wide hover:bg-white transition-colors duration-300 ${
                overHero ? 'bg-white/90 border border-white/40' : 'bg-white/70 border border-ink/10'
              }`}
              // No aria-label: the button already reads "Menu", and naming it
              // "Open menu" made the accessible name disagree with the visible
              // one (WCAG 2.5.3). Letting the name come from the content also
              // makes axe's mismatch rule inapplicable rather than merely
              // satisfied — the decorative dots below are aria-hidden, so they
              // stay out of the name.
            >
              Menu{' '}
              <span aria-hidden="true" className="tracking-[-0.2em] -mt-1.5 text-lg leading-none">
                ··
              </span>
            </button>
          </MagneticV2>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[120]"
            initial={{ clipPath: 'inset(0 0 100% 0)' }}
            animate={{ clipPath: 'inset(0 0 0% 0)' }}
            exit={{ clipPath: 'inset(0 0 100% 0)' }}
            transition={{ duration: 0.7, ease: EASE_LUSION }}
          >
            <div data-surface="dark" className="absolute inset-0 bg-card" />
            <div className="relative h-full flex flex-col justify-between p-6 md:p-10">
              <div className="flex items-center justify-between">
                <span className="font-grotesk font-bold text-sm md:text-base uppercase text-white/90">
                  Hamlet Hayrapetyan
                </span>
                <button
                  onClick={() => { setOpen(false); playClose(); }}
                  data-cursor="CLOSE"
                  className="h-10 md:h-11 px-5 rounded-full border border-white/25 text-white font-grotesk text-xs md:text-sm hover:bg-white hover:text-ink transition-colors duration-300"
                >
                  Close
                </button>
              </div>

              <nav className="flex flex-col gap-1 md:gap-2">
                {LINKS.map((l, i) => (
                  <div key={l.target} className="overflow-hidden">
                    <motion.button
                      initial={{ y: '110%' }}
                      animate={{ y: 0 }}
                      exit={{ y: '110%', transition: { duration: 0.3, delay: 0 } }}
                      transition={{ duration: 0.8, delay: 0.15 + i * 0.06, ease: EASE_OUT }}
                      onClick={() => go(l.target)}
                      data-cursor="GO"
                      className="group flex items-baseline gap-4 md:gap-6 text-left"
                    >
                      <span className="font-grotesk text-xs text-white/35 tabular-nums">0{i + 1}</span>
                      <span className="font-grotesk font-semibold text-[13vw] md:text-[6.5vw] leading-[1.02] tracking-tight text-white group-hover:text-electric group-hover:translate-x-3 transition-all inline-block" style={{ transitionDuration: '400ms' }}>
                        {l.label}
                      </span>
                    </motion.button>
                  </div>
                ))}
              </nav>

              {/* A full-screen menu should carry everything a visitor might
                  want without hunting: how to reach you, where to find your
                  work elsewhere, the CV, and the sound control. */}
              <div className="space-y-5">
                <div className="flex flex-wrap items-baseline gap-x-8 gap-y-3 pb-5 border-b border-white/10">
                  <a
                    href={`mailto:${email}`}
                    onClick={mailCopy.onClick}
                    data-cursor={mailCopy.copied ? 'COPIED' : 'COPY'}
                    className="font-grotesk text-lg md:text-2xl font-medium tracking-tight text-white hover:text-electric transition-colors"
                  >
                    <RollSwap swapped={mailCopy.copied}>{email}</RollSwap>
                  </a>
                  {phone && (
                    <a
                      href={`tel:${phone.replace(/[^+\d]/g, '')}`}
                      onClick={telCopy.onClick}
                      data-cursor={telCopy.copied ? 'COPIED' : 'COPY'}
                      className="font-grotesk text-base md:text-xl text-white/60 hover:text-white transition-colors tracking-tight"
                    >
                      <RollSwap swapped={telCopy.copied}>{phone}</RollSwap>
                    </a>
                  )}
                  <a
                    href={CV_PATH}
                    onClick={() => { setOpen(false); openCv(); }}
                    target="_blank"
                    rel="noopener"
                    data-cursor="OPEN"
                    className="ml-auto inline-flex items-center h-9 px-5 rounded-full border border-white/25 text-white font-grotesk text-[10px] uppercase tracking-[0.2em] hover:bg-white hover:text-ink transition-colors"
                  >
                    View CV
                  </a>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-x-8 gap-y-3 text-white/45 font-grotesk text-[10px] md:text-xs uppercase tracking-[0.25em]">
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                    {socials.map((s) => (
                      <a
                        key={s.name}
                        href={s.url}
                        target="_blank"
                        rel="noreferrer"
                        data-cursor="VISIT"
                        className="inline-flex items-center hover:text-white transition-colors"
                      >
                        {s.name}
                        <ArrowMark className="ml-1.5 opacity-70" />
                      </a>
                    ))}
                  </div>

                  <div className="flex items-center gap-6">
                    <span className="hidden sm:inline">Los Angeles, CA</span>
                    <button
                      onClick={() => {
                        const next = !sfxOff;
                        setSfxMuted(next);
                        setSfxOff(next);
                        if (!next) playOpen();
                      }}
                      data-cursor={sfxOff ? 'SOUND ON' : 'SOUND OFF'}
                      className="flex items-center gap-2 hover:text-white transition-colors"
                      aria-label={sfxOff ? 'Turn interface sound on' : 'Turn interface sound off'}
                    >
                      <span className="flex items-end gap-[2px] h-3">
                        {[0.5, 1, 0.7].map((h, i) => (
                          <motion.span
                            key={i}
                            className="w-[2px] bg-current rounded-full"
                            animate={sfxOff ? { height: 3 } : { height: [3, 11 * h, 3] }}
                            transition={sfxOff ? { duration: 0.2 } : { repeat: Infinity, duration: 0.9, delay: i * 0.12 }}
                          />
                        ))}
                      </span>
                      Sound {sfxOff ? 'off' : 'on'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default NavV2;
